// src/utils/theme.js
import { createTheme } from '@mui/material/styles';
import { getRoleColors } from './roleTheme';

const createAppTheme = (role = 'User', darkMode = false) => {
  const roleColors = getRoleColors(role);

  return createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: roleColors.primary,
        light: roleColors.primaryLight,
        dark: roleColors.primaryDark,
        contrastText: roleColors.contrastText,
      },
      secondary: {
        main: '#dc004e',
        light: '#ff6b9d',
        dark: '#9a0036',
        contrastText: '#ffffff',
      },
      success: {
        main: '#059669',
        light: '#10b981',
        dark: '#047857',
      },
      warning: {
        main: '#d97706',
        light: '#f59e0b',
        dark: '#b45309',
      },
      error: {
        main: '#dc2626',
        light: '#f87171',
        dark: '#b91c1c',
      },
      background: {
        default: darkMode ? '#000000' : '#F2F2F7',
        paper: darkMode ? '#1C1C1E' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#F9FAFB' : '#111827',
        secondary: darkMode ? '#9CA3AF' : '#6B7280',
      },
      divider: darkMode ? 'rgba(255, 255, 255, 0.10)' : 'rgba(15, 23, 42, 0.08)',
    },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: { fontWeight: 700, fontSize: '2.5rem' },
      h2: { fontWeight: 600, fontSize: '2rem' },
      h3: { fontWeight: 600, fontSize: '1.75rem' },
      h4: { fontWeight: 600, fontSize: '1.5rem' },
      h5: { fontWeight: 600, fontSize: '1.25rem' },
      h6: { fontWeight: 600, fontSize: '1rem' },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.5 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: darkMode
              ? 'none'
              : '0 1px 3px 0 rgba(0, 0, 0, 0.06)',
            borderRadius: 16,
            border: darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            boxShadow: 'none',
          },
          contained: {
            background: roleColors.primary,
            color: roleColors.contrastText,
            boxShadow: '0 10px 24px rgba(36, 41, 157, 0.28)',
            '&:hover': {
              background: roleColors.primaryDark,
              boxShadow: '0 10px 24px rgba(36, 41, 157, 0.35)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#F9FAFB',
              color: darkMode ? '#9CA3AF' : '#6B7280',
              fontWeight: 600,
            },
          },
        },
      },
    },
    custom: {
      roleColors,
    },
  });
};

export default createAppTheme;
